import React, { FC } from 'react';
import { useQuery } from '@apollo/client';
import PageContainer from 'components/@layout/PageContainer';
import GET_KEYBOARDING_STUDENT_SETTINGS from 'graph/settings/key/get-keyboarding-student-settings-query';
import { Student } from 'types/schema-generated-types';
import HomeAccess from './HomeAccess';
import JumpIntoKeyboarding from './JumpIntoKeyboarding';
import StopAndReview from './StopAndReview';
import OneHandedTyping from './OneHandedTyping';

interface KeyboardingSettingsProps {
  classroomId: number;
  student: Student;
  productCurriculumCode: string;
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const StudentKeyboardingSettings: FC<KeyboardingSettingsProps> = ({
  student,
  productCurriculumCode,
  expanded,
  handleChangeExpanded
}) => {
  const { id: studentId } = student;

  const { data, loading } = useQuery(GET_KEYBOARDING_STUDENT_SETTINGS, {
    variables: {
      where: {
        studentId: Number(studentId)
      }
    },
    // TODO: Using network-only here to avoid caching because some classroom and student level settings (mostly Home Access)
    // are dependent/derived from one another. In other words, changing the setting at one level will affect the setting at the
    // other level. Figuring out the required cache updates to keep these synced this seemed tricky, so avoiding the cache
    // altogether for now.
    fetchPolicy: 'network-only'
  });

  return (
    <PageContainer loading={loading}>
      <HomeAccess
        student={student}
        productCurriculumCode={productCurriculumCode}
        setting={data?.keyStudentSettings.homeUse}
        expanded={expanded}
        handleChangeExpanded={handleChangeExpanded}
      />
      <JumpIntoKeyboarding
        student={student}
        productCurriculumCode={productCurriculumCode}
        setting={data?.keyStudentSettings.skipJumpIn}
        expanded={expanded}
        handleChangeExpanded={handleChangeExpanded}
      />
      <StopAndReview
        student={student}
        productCurriculumCode={productCurriculumCode}
        setting={data?.keyStudentSettings.stopAndReview}
        expanded={expanded}
        handleChangeExpanded={handleChangeExpanded}
      />
      <OneHandedTyping
        student={student}
        setting={data?.keyStudentSettings.oneHandedTypingHand}
        expanded={expanded}
        handleChangeExpanded={handleChangeExpanded}
      />
    </PageContainer>
  );
};

export default StudentKeyboardingSettings;
